import { UploadChangeParam, UploadFile } from 'antd/lib/upload/interface';
import { Upload } from 'antd';
import { useEffect, useState } from 'react';
import {
  useNotificationService
} from '../../../../services/notification.service';
import { listAccordionFormStore } from '../../store/list-accordion-form.store';
import { useTranslation } from 'react-i18next';

interface UseUploadContainerProps {
  idMatricula: number;
}

export default function useUploadContainer({ idMatricula }: UseUploadContainerProps) {
  const notification = useNotificationService();
  const { updateAccordionForm, findAccordionById } = listAccordionFormStore();
  const [fileList, setFileList] = useState<UploadFile[]>([]);
  const { t } = useTranslation('gestao_de_aulas');

  const extractFileUrl = (response: unknown): string | undefined => {
    if (!response)
      return undefined;

    if (typeof response === 'string')
      return response.replace(/^"|"$/g, '');

    if (typeof response !== 'object')
      return undefined;

    const payload = response as Record<string, unknown>;
    const candidate =
      payload.url
      || payload.link
      || (payload.data as Record<string, unknown>)?.url
      || (payload.data as Record<string, unknown>)?.link;

    return typeof candidate === 'string' ? candidate : undefined;
  };

  const onChange = ({ file, fileList: nextFileList }: UploadChangeParam) => {
    setFileList(nextFileList as UploadFile[]);
    const fileUrl = extractFileUrl(file.response);
    const { status } = file;

    if (status === 'done')
      return handleSuccess(fileUrl, file.name);

    if (status === 'removed') {
      updateAccordionForm(idMatricula, 'projetoNomeArquivo', undefined);
      return updateAccordionForm(idMatricula, 'projetoLink', undefined);
    }

    if (status === 'error')
      return handleError();
  };

  const handleSuccess = (fileUrl?: string, fileName?: string) => {
    if (!fileUrl)
      return handleError();

    notification({
      description: t('gestao_de_aulas:uploadSuccessDescription'),
      type: 'success',
      message: t('gestao_de_aulas:uploadSuccessTitle')
    });
    updateAccordionForm(idMatricula, 'projetoLink', fileUrl);
    updateAccordionForm(idMatricula, 'projetoNomeArquivo', fileName);
  };

  const handleError = () => {
    notification({
      description: t('gestao_de_aulas:uploadErrorDescription'),
      type: 'error',
      message: t('gestao_de_aulas:uploadErrorTitle')
    });
  };

  const beforeUpload = (file: UploadFile) => {
    const TEN_MEGABYTES_IN_BYTES = 10 * 1024 * 1024;
    const allowedExtensions = ['jpg', 'jpeg', 'png', 'mp4', 'webp'];
    const fileExtension = file.name?.split('.').pop()?.toLowerCase();

    if (file.size > TEN_MEGABYTES_IN_BYTES) {
      notification({
        description: t('gestao_de_aulas:uploadMaxSizeError'),
        type: 'error',
        message: t('gestao_de_aulas:uploadErrorTitle')
      });
      return Upload.LIST_IGNORE;
    }

    if (!fileExtension || !allowedExtensions.includes(fileExtension)) {
      notification({
        description: t('gestao_de_aulas:uploadInvalidExtensionError'),
        type: 'error',
        message: t('gestao_de_aulas:uploadErrorTitle')
      });
      return Upload.LIST_IGNORE;
    }

    return true;
  };

  const projectLinkValue = (): UploadFile => {
    const projetoLink = findAccordionById(idMatricula)?.form?.projetoLink;
    const projetoNomeArquivo = findAccordionById(idMatricula)?.form?.projetoNomeArquivo;

    if (!projetoLink || typeof projetoLink !== 'string')
      return null;

    const name = projetoNomeArquivo || projetoLink.split('/').pop() || '';

    return {
      uid: String(idMatricula),
      name,
      status: 'done',
      url: projetoLink
    };
  };

  const projetoLink = findAccordionById(idMatricula)?.form?.projetoLink;
  const projetoNomeArquivo = findAccordionById(idMatricula)?.form?.projetoNomeArquivo;

  useEffect(() => {
    if (!projetoLink) {
      setFileList((currentList) => {
        const hasUploading = currentList.some((file) => file.status === 'uploading');
        return hasUploading ? currentList : [];
      });
      return;
    }

    const persistedFile = projectLinkValue();
    setFileList(persistedFile ? [persistedFile] : []);
  }, [idMatricula, projetoLink, projetoNomeArquivo]);

  const currentAccordion = findAccordionById(idMatricula);
  const isPresencaAusente = currentAccordion?.form?.presenca === 'AST';
  const disabled = !!(isPresencaAusente || currentAccordion?.disabled);

  return {
    upload: {
      before: beforeUpload,
      onChange
    },
    form: {
      fileList,
      disabled
    }
  };
}
